import {
  Component,
  OnInit,
  signal,
  WritableSignal,
  inject,
  DOCUMENT,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ConfigService } from '../../shared/services/config.service';
import { AuthService } from '@auth0/auth0-angular';
import { NgTemplateOutlet } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, take } from 'rxjs';
import { LoadingCircleComponent } from '../../layout/loading-circle/loading-circle.component';
import { AlertService } from '../../shared/services/alert.service';
import { ProfileService } from '../../shared/services/profile.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-verify-email',
  imports: [TranslateModule, NgTemplateOutlet, LoadingCircleComponent],
  templateUrl: './verify-email.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './verify-email.component.scss',
})
export class VerifyEmailComponent implements OnInit {
  private doc = inject<Document>(DOCUMENT);
  private auth = inject(AuthService);
  private profileService = inject(ProfileService);
  private alert = inject(AlertService);
  private translate = inject(TranslateService);
  private router = inject(Router);

  readonly userEmail: string;

  logoURL: string;

  loading: WritableSignal<boolean> = signal(false);

  resending: WritableSignal<boolean> = signal(false);

  constructor() {
    this.userEmail = this.router.getCurrentNavigation()?.extras.state
      ? this.router.getCurrentNavigation()?.extras.state['unverifiedEmail']
      : undefined;
  }

  ngOnInit(): void {
    if (!this.userEmail) {
      this.router.navigateByUrl('/login').catch();
      return;
    }

    this.loading.set(true);
    const oemId: string = ConfigService.getIdConfig();
    this.logoURL = `../../../assets-common/${oemId}/logo.svg`;

    this.translate.onDefaultLangChange.pipe(take(1)).subscribe({
      next: () => this.loading.set(false),
    });
  }

  continue(): void {
    this.auth.loginWithRedirect({
      appState: { target: 'profile' },
      authorizationParams: {
        ui_locales: ConfigService.getAuthUiLocale(this.translate.currentLang),
      },
    });
  }

  resend(): void {
    if (this.resending()) return;

    this.resending.set(true);
    this.profileService
      .sendVerificationEmail(this.userEmail)
      .pipe(
        take(1),
        finalize(() => this.resending.set(false)),
      )
      .subscribe({
        next: () => this.alert.info(this.translate.instant('VERIFY_EMAIL.EMAIL_SENT')),
        error: () => this.alert.error(this.translate.instant('VERIFY_EMAIL.SENT_ERROR')),
      });
  }

  logout(): void {
    this.auth.logout({ logoutParams: { returnTo: this.doc.location.origin } });
  }
}
